import React, { useState } from 'react';
import { chatCard, chatFocus, chatLabel } from './chat-theme';
import CardHeader from './card-header';
import { HiCheck, HiChevronDown, HiOutlineListBullet, HiXMark } from 'react-icons/hi2';

export function Spinner({ className = 'h-3.5 w-3.5' }) {
    return (
        <span
            aria-hidden="true"
            className={`inline-block animate-spin rounded-full border-2 border-gray-300 border-t-gray-900 ${className}`}
        />
    );
}

function StatusIcon({ status }) {
    if (status === 'running') return <Spinner />;
    if (status === 'failed') return <HiXMark className="h-3.5 w-3.5 text-red-500" aria-label="failed" />;
    return <HiCheck className="h-3.5 w-3.5 text-lime-700 dark:text-brand-lime" aria-label="done" />;
}

/** The copilot's plan (write_todos) as a checklist. */
export function Plan({ todos }) {
    if (!todos?.length) return null;
    const items = todos.filter(todo => todo && typeof todo === 'object');
    const done = items.filter(todo => todo.status === 'completed').length;
    return (
        <div className={chatCard('planning')}>
            <CardHeader icon={HiOutlineListBullet} title="Plan" meta={`${done} of ${items.length} done`} />
            <ul className="space-y-1.5">
                {items.map((todo, index) => (
                    <li key={index} className="flex items-start gap-2.5 text-[0.85rem]">
                        <span className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center">
                            {todo.status === 'completed'
                                ? <span className="flex h-4 w-4 items-center justify-center rounded-full bg-gray-900 dark:bg-brand-lime"><HiCheck className="h-2.5 w-2.5 text-brand-lime" /></span>
                                : todo.status === 'in_progress'
                                    ? <Spinner className="h-3.5 w-3.5" />
                                    : <span className="h-3.5 w-3.5 rounded-full border-[1.5px] border-gray-300" />}
                        </span>
                        <span className={todo.status === 'completed'
                            ? 'text-gray-400 line-through decoration-gray-300'
                            : todo.status === 'in_progress' ? 'font-medium text-gray-900' : 'text-gray-700'}>
                            {String(todo.content ?? '')}
                        </span>
                    </li>
                ))}
            </ul>
        </div>
    );
}

/**
 * Tool and sub-agent steps: a one-line summary that expands into the full list.
 *
 * The runtime reports a tool only once the model has finished writing its call, which can
 * take tens of seconds (a clarification question with its options). While the turn runs
 * with no step open, the model is working on the next one, so say so instead of showing a
 * finished-looking list.
 */
export function Steps({ steps, running }) {
    const [open, setOpen] = useState(false);
    if (!steps?.length) return null;
    const current = [...steps].reverse().find(step => step.status === 'running');
    const thinking = running && !current;
    const failed = steps.filter(step => step.status === 'failed').length;
    const summary = running
        ? current?.label || 'Thinking…'
        : `${steps.length} step${steps.length === 1 ? '' : 's'}${failed ? ` · ${failed} failed` : ''}`;

    return (
        <div className="text-[0.8rem] text-gray-500">
            <button
                type="button"
                onClick={() => setOpen(value => !value)}
                className={`-ml-2 inline-flex max-w-full items-center gap-2 rounded-lg px-2 py-1 font-medium transition-colors hover:bg-gray-100 hover:text-gray-800 ${running ? 'text-gray-700' : ''} ${chatFocus}`}
                aria-expanded={open}
            >
                {running ? <Spinner /> : failed
                    ? <HiXMark className="h-3.5 w-3.5 shrink-0 text-red-500" />
                    : <HiCheck className="h-3.5 w-3.5 shrink-0 text-lime-700 dark:text-brand-lime" />}
                <span className="truncate">{summary}</span>
                <HiChevronDown className={`h-3.5 w-3.5 shrink-0 opacity-60 transition-transform ${open ? 'rotate-180' : ''}`} />
            </button>
            {open && (
                <ul className="ml-[3px] mt-1.5 space-y-1.5 border-l border-gray-200 pl-3.5">
                    {steps.map(step => (
                        <li key={step.id} className="flex items-start gap-2">
                            <span className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center"><StatusIcon status={step.status} /></span>
                            <span className="text-gray-600">
                                {step.kind === 'subagent' && <span className="mr-1.5 rounded-md bg-gray-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-gray-500">Agent</span>}
                                {String(step.label ?? '')}
                                {step.error && <span className="block text-red-600 dark:text-red-400">{String(step.error)}</span>}
                            </span>
                        </li>
                    ))}
                    {thinking && (
                        <li className="flex items-start gap-2 text-gray-500">
                            <span className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center"><Spinner /></span>
                            <span>Thinking…</span>
                        </li>
                    )}
                </ul>
            )}
        </div>
    );
}

const ACTION_VERB = { created: 'Created', updated: 'Updated', deleted: 'Deleted' };
const ACTION_DOT = { created: 'bg-lime-500 dark:bg-brand-lime', deleted: 'bg-red-500' };

/** What the copilot changed in this turn. */
export function Changes({ changes }) {
    if (!changes?.length) return null;
    return (
        <div className="flex flex-wrap gap-1.5">
            {changes.map((change, index) => (
                <span
                    key={index}
                    className={`${chatLabel()} max-w-full break-words`}
                >
                    <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${ACTION_DOT[change?.action] || 'bg-gray-400'}`} />
                    {ACTION_VERB[change?.action] || 'Changed'} {String(change?.kind || '').replace(/_/g, ' ')}
                    {change?.name ? `: ${String(change.name)}` : ''}
                </span>
            ))}
        </div>
    );
}
